<script lang="ts">
  // BJL-CHECK: Button (used), Spinner (used), Badge, Tag, Drawer, List. Same shell as
  // FerryLandingSheet and BusStopSheet, for the same reasons (artifacts/deviations/
  // BusStopSheet.md): the drag-resizable sheet is something Drawer can't express, and a
  // branch badge is painted in the railroad's colour — data — so it is RailRouteBadge.
  import { Button } from '@byjackli-labs/ui/Button'
  import { Spinner } from '@byjackli-labs/ui/Spinner'
  import { Colors, getRouteLongName } from '../../ui/colors'
  import { cardChrome, panelFrame, ROW_RULE, Type } from '../../ui/theme'
  import { safeBottom } from '../../ui/safeArea'
  import { formatCountdown, formatShortClock } from '../../ui/transitTime'
  import { useBackHandler } from '../../hooks/useBackHandler.svelte'
  import { useRailStore } from '../../store/rail'
  import { api, type RailDeparture } from '../../services/api'
  import { storeSelect } from '../../lib/store.svelte'
  import { useResizableSheet } from '../../lib/resizableSheet.svelte'
  import type { TransferContext } from './timetable'
  import type { RailStationInfo } from './stationInfo'
  import TripTimetable from './TripTimetable.svelte'
  import TransferBanner from './TransferBanner.svelte'
  import RailRouteBadge from '../RailRouteBadge.svelte'
  import SheetBackButton from '../SheetBackButton.svelte'

  interface Props {
    station: RailStationInfo
    onClose: () => void
    /** Reopen the sheet on another station, chosen from a train's timetable. */
    onTransfer?: (station: RailStationInfo, ctx: TransferContext | null) => void
    /** Set when reached by transferring: departures before the rider gets here are hidden. */
    transfer?: TransferContext | null
  }

  const { station, onClose, onTransfer, transfer }: Props = $props()

  const _wh = typeof window !== 'undefined' ? window.innerHeight : 800
  const sheet = useResizableSheet({
    minHeight: 80,
    defaultHeight: Math.round(_wh * 0.5),
    maxHeight: Math.round(_wh * 0.92),
  })

  // The trains report every ~30 s; polling faster only re-reads the same snapshot.
  const REFRESH_MS = 30_000

  const network = storeSelect(useRailStore, (s) => s.network)
  const info = $derived(network.value?.stations.find((s) => s.id === station.id))
  const agencyName = $derived(info?.agency === 'MNR' ? 'Metro-North' : 'LIRR')

  let departures = $state<RailDeparture[] | null>(null)
  let loading = $state(true)
  let failed = $state(false)
  let selectedTrip = $state<RailDeparture | null>(null)
  /** One branch, or every branch — a busy terminal's board is 60 trains on 10 branches. */
  let branch = $state<string | null>(null)

  useBackHandler(() => selectedTrip !== null, () => { selectedTrip = null })

  const stationId = $derived(station.id)

  let bodyEl = $state<HTMLDivElement | null>(null)
  const drillKey = $derived(`${stationId}|${selectedTrip ? selectedTrip.tripId : 'board'}`)
  $effect(() => {
    drillKey
    if (bodyEl) bodyEl.scrollTop = 0
  })

  $effect(() => {
    const id = stationId
    let live = true
    const load = () => {
      api
        .getRailDepartures(id)
        .then((d) => { if (live) { departures = d.departures; failed = false } })
        .catch(() => { if (live) { departures = departures ?? []; failed = true } })
        .finally(() => { if (live) loading = false })
    }
    loading = true
    departures = null
    selectedTrip = null
    branch = null
    load()
    const timer = setInterval(load, REFRESH_MS)
    return () => {
      live = false
      clearInterval(timer)
    }
  })

  // A transferring rider can see everything with one tap; a fresh transfer re-arms it.
  let showAll = $state(false)
  $effect(() => {
    transfer
    showAll = false
  })
  const cutoff = $derived(transfer && !showAll ? transfer.arriveAt : null)

  // Branches offered as filters: the ones on the board right now, in route order.
  const branches = $derived.by(() => {
    const on = new Set((departures ?? []).map((d) => d.routeId))
    return (info?.routes ?? []).filter((r) => on.has(r))
  })
  const visible = $derived(
    (departures ?? []).filter((d) =>
      (cutoff == null || d.time >= cutoff) && (!branch || d.routeId === branch)),
  )

  /** "On time", "4 min late", "Cancelled" or "Scheduled". */
  function status(d: RailDeparture): { text: string; color: string } {
    if (d.cancelled) return { text: 'Cancelled', color: Colors.danger }
    if (!d.realtime) return { text: 'Scheduled', color: Colors.textMuted }
    const late = Math.round((d.time - d.scheduledTime) / 60)
    return late >= 2
      ? { text: `${late} min late`, color: Colors.danger }
      : { text: 'Live · on time', color: Colors.success }
  }

  function details(d: RailDeparture): string {
    return [
      d.trainNumber ? `Train ${d.trainNumber}` : '',
      d.track ? `Track ${d.track}` : '',
      d.peak ? 'Peak' : '',
    ].filter(Boolean).join(' · ')
  }
</script>

{#snippet departureTime(d: RailDeparture)}
  {@const label = formatCountdown(d.time, { fallback: '--' })}
  <!-- A countdown under an hour, a clock time beyond it (and always on a transfer). -->
  {#if d.cancelled}
    <span style="color: {Colors.textMuted}; {Type.title}; text-decoration: line-through;">{formatShortClock(d.scheduledTime)}</span>
  {:else if cutoff == null && d.time - Date.now() / 1000 < 3600}
    {@const isNow = label === 'Now'}
    <span style="color: {isNow ? Colors.success : Colors.text}; {Type.title};">
      {isNow ? label : label.replace(' min', '')}
    </span>
    {#if !isNow}<span style="color: {Colors.textSecondary}; {Type.caption};"> min</span>{/if}
  {:else}
    <span style="color: {Colors.text}; {Type.title};">{formatShortClock(d.time)}</span>
  {/if}
{/snippet}

<div
  style="
    position: fixed; bottom: 0; left: 0; right: 0;
    height: {sheet.height}px;
    background-color: {Colors.ground};
    {panelFrame('top')}
    z-index: 1000;
    display: flex; flex-direction: column;
    transition: {sheet.dragging ? 'none' : 'height 0.22s cubic-bezier(0.4,0,0.2,1)'};
  "
>
  <!-- Handle row with the chrome on it, laid out exactly as the other stop sheets'. -->
  <div style="position: relative; flex-shrink: 0; display: flex; align-items: center; padding: 4px 18px 2px;">
    <div style="display: flex; align-items: center; flex-shrink: 0;">
      {#if selectedTrip}
        <SheetBackButton onclick={() => { selectedTrip = null }} label="Back to departures" />
      {/if}
    </div>
    <div
      onpointerdown={sheet.onHandleDown}
      role="separator"
      aria-label="Resize station panel"
      style="flex: 1; align-self: stretch; min-height: 34px; cursor: ns-resize; user-select: none; touch-action: none;"
    ></div>
    <div
      style="
        position: absolute; left: 50%; top: 14px; transform: translateX(-50%);
        width: 36px; height: 4px; border-radius: 2px;
        background-color: rgba(255,255,255,0.25); pointer-events: none;
      "
    ></div>
    <Button variant="text" size="sm" iconOnly onclick={onClose} aria-label="Close station panel">✕</Button>
  </div>

  <div style="padding: 4px 20px 0; flex-shrink: 0;">
    <div style="color: {Colors.textMuted}; {Type.caption};">
      {agencyName} station{info?.accessible ? ' · ♿ Accessible' : ''}
    </div>
    <div style="color: {Colors.text}; {Type.heading}; margin-top: 2px; overflow-wrap: anywhere;">
      {station.name}
    </div>
  </div>

  {#if !selectedTrip && branches.length > 1}
    <!-- Branch filter: plain toggle buttons, the one control a 60-train board needs. -->
    <div style="display: flex; gap: 8px; padding: 12px 20px 0; flex-shrink: 0; overflow-x: auto;">
      <button
        onclick={() => { branch = null }}
        aria-pressed={branch === null}
        style="
          flex-shrink: 0; padding: 5px 10px; border-radius: 8px; cursor: pointer;
          border: 1px solid {branch === null ? Colors.primary : Colors.borderStrong};
          background: {branch === null ? Colors.surfaceElevated : 'none'};
          color: {Colors.text}; {Type.caption}; font-weight: 700;
        "
      >All</button>
      {#each branches as routeId (routeId)}
        <button
          onclick={() => { branch = branch === routeId ? null : routeId }}
          aria-pressed={branch === routeId}
          aria-label="Only {getRouteLongName(routeId)} trains"
          style="
            flex-shrink: 0; display: flex; align-items: center; gap: 6px;
            padding: 3px 8px 3px 4px; border-radius: 8px; cursor: pointer;
            border: 1px solid {branch === routeId ? Colors.primary : Colors.borderStrong};
            background: {branch === routeId ? Colors.surfaceElevated : 'none'};
            color: {Colors.textSecondary}; {Type.caption}; white-space: nowrap;
          "
        >
          <RailRouteBadge {routeId} size={18} />
          {getRouteLongName(routeId).replace(/ Branch$/, '')}
        </button>
      {/each}
    </div>
  {/if}

  <div bind:this={bodyEl} style="overflow-y: auto; flex: 1; min-height: 0; padding: 0 20px {safeBottom(28)};">
    {#if selectedTrip}
      <div style="display: flex; align-items: center; gap: 10px; padding: 4px 0 10px;">
        <RailRouteBadge routeId={selectedTrip.routeId} size={28} />
        <div style="flex: 1; min-width: 0;">
          <div style="color: {Colors.text}; {Type.title}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            To {selectedTrip.headsign}
          </div>
          <div style="color: {Colors.textMuted}; {Type.caption};">
            {[getRouteLongName(selectedTrip.routeId), details(selectedTrip)].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
      <TripTimetable
        source={{ kind: 'rail', tripId: selectedTrip.tripId }}
        routeId={selectedTrip.routeId}
        currentStopId={station.id}
        onTransfer={(stopId, stopName, ctx) => onTransfer?.({ id: stopId, name: stopName }, ctx)}
      />
    {:else if loading && !departures}
      <div style="display: flex; align-items: center; gap: 8px; padding: 10px 0; color: {Colors.textMuted}; {Type.body};">
        <Spinner size="sm" label="Loading trains" /> Loading trains…
      </div>
    {:else}
      {#if transfer}
        <TransferBanner ctx={transfer} active={!showAll} onToggle={() => { showAll = !showAll }} />
      {/if}
      {#if visible.length > 0}
        <!-- The railroad's own board: every train in time order, both directions, where it
             is going on the leading edge and when it leaves on the trailing one. -->
        <div style="{cardChrome()} padding: 0 14px; margin-top: 12px;">
          {#each visible as d, i (d.tripId + d.scheduledTime)}
            {@const st = status(d)}
            <button
              onclick={() => { selectedTrip = d }}
              aria-label="See the stops of the {formatShortClock(d.scheduledTime)} train to {d.headsign}"
              style="
                display: flex; align-items: center; gap: 12px; padding: 11px 0;
                background: none; border: none;
                {i === visible.length - 1 ? '' : `border-bottom: ${ROW_RULE};`}
                cursor: pointer; text-align: left; width: 100%;
                opacity: {d.cancelled ? 0.65 : 1};
              "
            >
              <RailRouteBadge routeId={d.routeId} size={24} />
              <div style="flex: 1; min-width: 0;">
                <div style="color: {Colors.text}; {Type.bodyLg}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  To {d.headsign}
                </div>
                <div style="color: {Colors.textMuted}; {Type.caption}; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  <span style="color: {st.color};">{st.text}</span>{details(d) ? ` · ${details(d)}` : ''}
                </div>
              </div>
              <div style="text-align: right; flex-shrink: 0;">{@render departureTime(d)}</div>
              <span aria-hidden="true" style="flex-shrink: 0; color: rgba(255,255,255,0.35); font-size: 15px;">›</span>
            </button>
          {/each}
        </div>
      {:else}
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
          {failed
            ? `Couldn’t reach the ${agencyName} timetable. Retrying…`
            : cutoff != null
              ? `No trains leave here after ${formatShortClock(cutoff)} in the next few hours.`
              : 'No trains leave here in the next few hours.'}
        </div>
      {/if}
    {/if}
  </div>
</div>
